import { useEffect, useEffectEvent, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@legal-500/legal500-design-system";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { usePermissions } from "@/hooks/usePermissions";
import { CmsEmptyState } from "../../../components/ResearchCms";
import { UnsavedChangesModal } from "../../../components/ResearchCms/UnsavedChangesModal/UnsavedChangesModal";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import { ComparePanel } from "./components/BpaImport/ComparePanel";
import { ContentSection } from "./components/BpaImport/ContentSection";
import { messageOf } from "./components/BpaImport/importText";
import shared from "./components/BpaImport/importShared.module.css";
import { ImportSummaryBar } from "./components/BpaImport/ImportSummaryBar";
import { OrganisationsSection } from "./components/BpaImport/OrganisationsSection";
import { SourceSection } from "./components/BpaImport/SourceSection";
import { useImportData, useImportPreviewQuery } from "./components/BpaImport/useImportData";
import { useOrganisationsDisplay } from "./components/BpaImport/useOrganisationsDisplay";
import { useComparePanelMotion } from "./components/BpaImport/useComparePanelMotion";
import { useImportChoices } from "./components/BpaImport/useImportChoices";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import { useSplitImportContext } from "./hooks/useSplitImportContext";
import {
  firmKey,
  buildImportSelection,
  canMove,
  canReplaceTargetContent,
  defaultJurisdiction,
  describeImportSummary,
  type ImportCounts,
  type ImportMode,
  type ImportPreview,
  type ImportPreviewOrg,
} from "./utils/bpaImport";
import {
  importBlockedReason,
  moveBlockedReason,
  modeForSource,
} from "./utils/bpaImportEditionStatus";
import {
  dropStaleChoices,
  firmControl,
  previewTotals,
  readItemChoiceRefusal,
  resetRefusedChoices,
  withFirmControl,
} from "./utils/bpaImportItemControl";
import { cancelDestination, isImportDirty } from "./utils/bpaImportNavigation";
import {
  applicableChoices,
  clearImportChoices,
  loadImportChoices,
  saveImportChoices,
} from "./utils/bpaImportSessionStore";
import { type ImportPurpose, resolveImportUrl, writeImportUrl } from "./utils/bpaImportUrlState";
import { mergeMoveLine, toMergeRow, type MergeRow, mergeBadge } from "./utils/bpaMerge";
import { splitImportHint, splitMoveLine } from "./utils/bpaSplit";
import styles from "./CmsBpaImportPage.module.css";

type Exit = { to?: string };

type UrlContext = {
  purpose: ImportPurpose | null;
  hub: string | null;
  blocked: { reason: string; hub: string } | null;
};

type SeenPreview = { sourceId: string; data: ImportPreview };

const NO_ORGS: ImportPreviewOrg[] = [];

const NO_COUNTS: ImportCounts = { submissions: 0, rankings: 0, commentary: 0 };

export function CmsBpaImportPage() {
  const { editionId = "", bpaId = "" } = useParams<{ editionId: string; bpaId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const choices = useImportChoices();
  const { state } = choices;
  const [hydrated, setHydrated] = useState(false);
  const [urlContext, setUrlContext] = useState<UrlContext>({
    purpose: null,
    hub: null,
    blocked: null,
  });
  const [modeNotice, setModeNotice] = useState<string | null>(null);
  const [seenPreview, setSeenPreview] = useState<SeenPreview | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [exit, setExit] = useState<Exit | null>(null);
  const [refusalResets, setRefusalResets] = useState(0);
  const [pickedPublicationId, setPickedPublicationId] = useState<string | null>(null);
  const [pickedEditionId, setPickedEditionId] = useState<string | null>(() =>
    searchParams.get("sourceEdition"),
  );
  const compareTitleRef = useRef<HTMLHeadingElement>(null);
  const compareButtons = useRef(new Map<string, HTMLButtonElement>());
  const compareOpener = useRef<HTMLElement | null>(null);

  const data = useImportData({ editionId, bpaId, pickedEditionId });
  const {
    targetQuery,
    publicationsQuery,
    publications,
    sourceEditionId,
    sourceInAnotherEdition,
    sourceEdition,
    sourceBpasQuery,
    sourceRows,
    target,
    candidates,
    targetName,
  } = data;
  const targetPublication = publications.find(pub =>
    pub.editions.some(edition => edition.editionId === editionId),
  );
  const sourceEditionStatus = sourceEdition?.edition.status ?? null;
  const sourcePublication =
    publications.find(pub => pub.id === pickedPublicationId) ??
    sourceEdition?.publication ??
    targetPublication ??
    null;
  const source = candidates.find(bpa => bpa.id === state.sourceEditionBpaId) ?? null;
  const sourceName = data.sourceNameOf(state.sourceEditionBpaId);

  const publicationOptions = publications
    .filter(pub => pub.editions.length > 0)
    .map(pub => ({ value: pub.id, label: pub.name }));
  const editionOptions = (sourcePublication?.editions ?? []).map(edition => ({
    value: edition.editionId,
    label: edition.editionName ?? edition.editionId,
  }));

  const cachedContext = data.urlContext(canMove(hasPermission));
  const cachedResolution = cachedContext ? resolveImportUrl(searchParams, cachedContext) : null;
  const waitsForFreshTarget =
    targetQuery.isFetching &&
    (searchParams.get("purpose") === "split" || searchParams.get("purpose") === "merge") &&
    cachedResolution?.purpose === null;
  const urlReady =
    target !== null &&
    !waitsForFreshTarget &&
    (publicationsQuery.isSuccess || publicationsQuery.isError) &&
    (!sourceInAnotherEdition || !sourceBpasQuery.isPending);
  const hydrateFromUrl = useEffectEvent(() => {
    const context = data.urlContext(canMove(hasPermission));
    if (!context) return;
    const resolved = resolveImportUrl(searchParams, context);
    choices.hydrate({
      sourceEditionBpaId: resolved.sourceEditionBpaId,
      kinds: resolved.kinds,
      mode: resolved.mode,
    });
    setUrlContext({ purpose: resolved.purpose, hub: resolved.hub, blocked: resolved.blocked });
    setModeNotice(resolved.notice);
    setHydrated(true);
  });
  useEffect(() => {
    if (!hydrated && urlReady) hydrateFromUrl();
  }, [hydrated, urlReady]);

  useEffect(() => {
    if (!hydrated) return;
    const next = writeImportUrl(
      {
        sourceEditionBpaId: state.sourceEditionBpaId,
        sourceEditionId,
        kinds: state.kinds,
        mode: state.mode,
        purpose: urlContext.purpose,
        hub: urlContext.hub,
      },
      editionId,
    );
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [
    hydrated,
    state.sourceEditionBpaId,
    sourceEditionId,
    state.kinds,
    state.mode,
    urlContext,
    editionId,
    searchParams,
    setSearchParams,
  ]);

  function changeSourceEdition(nextEditionId: string) {
    if (nextEditionId === sourceEditionId) return;
    setPickedEditionId(nextEditionId);
    choices.clearSource();
  }

  function changeSourcePublication(publicationId: string) {
    const publication = publications.find(pub => pub.id === publicationId);
    if (!publication || publication.id === sourcePublication?.id) return;
    setPickedPublicationId(publicationId);
    const keepsTargetEdition = publication.editions.some(
      edition => edition.editionId === editionId,
    );
    changeSourceEdition(
      keepsTargetEdition ? editionId : (publication.editions[0]?.editionId ?? ""),
    );
  }

  function pickSource(editionBpaId: string) {
    if (editionBpaId === state.sourceEditionBpaId) return;
    choices.chooseSource(editionBpaId);
    const next = modeForSource(state.mode, sourceEditionStatus);
    if (next.notice) {
      choices.setMode(next.mode);
      setModeNotice(next.notice);
    }
  }

  function changeMode(mode: ImportMode) {
    choices.setMode(mode);
    setModeNotice(null);
  }

  const latestPreview =
    seenPreview?.sourceId === state.sourceEditionBpaId ? seenPreview.data : null;
  const pruned = latestPreview ? dropStaleChoices(state, latestPreview) : { state, dropped: 0 };
  const importState = pruned.state;
  const droppedCount = pruned.dropped;
  const dropStaleNow = useEffectEvent(() => choices.dropStale(pruned.state, pruned.dropped));
  useEffect(() => {
    if (droppedCount > 0) dropStaleNow();
  }, [droppedCount]);

  const listSelection =
    state.kinds.length > 0 ? buildImportSelection({ ...importState, unticked: [] }) : null;
  const preview = useImportPreviewQuery(target?.id, listSelection);
  const freshPreview = preview.isSuccess && !preview.isPlaceholderData ? preview.data : null;
  const refusal =
    preview.isError && listSelection ? readItemChoiceRefusal(preview.error, listSelection) : null;
  const previewData = preview.data ?? (refusal ? latestPreview : null);
  const sourcePreview = freshPreview ?? latestPreview;
  const fromOtherPublication =
    sourcePublication !== null && sourcePublication.id !== targetPublication?.id;
  const scoresComeAcross =
    !fromOtherPublication || (sourcePreview !== null && !sourcePreview.summary.sarsScaleMismatch);

  const rememberPreview = useEffectEvent((data: ImportPreview) => {
    if (state.sourceEditionBpaId) setSeenPreview({ sourceId: state.sourceEditionBpaId, data });
  });
  useEffect(() => {
    if (freshPreview) rememberPreview(freshPreview);
  }, [freshPreview]);

  const restoreFromSession = useEffectEvent((data: ImportPreview) => {
    const sourceId = state.sourceEditionBpaId;
    if (!target || !sourceId) return;
    const saved = loadImportChoices(target.id, sourceId);
    if (saved) choices.restore(applicableChoices(saved, data), sourceId);
    else choices.markRestored(sourceId);
  });
  useEffect(() => {
    if (freshPreview && choices.restoredFor !== state.sourceEditionBpaId) {
      restoreFromSession(freshPreview);
    }
  }, [freshPreview, choices.restoredFor, state.sourceEditionBpaId]);

  const targetId = target?.id ?? null;
  const savingOpen = exit === null && choices.restoredFor === state.sourceEditionBpaId;
  useEffect(() => {
    if (!savingOpen || !targetId || !state.sourceEditionBpaId) return;
    saveImportChoices(targetId, state.sourceEditionBpaId, {
      unticked: state.unticked,
      choices: state.choices,
      itemChoices: state.itemChoices ?? {},
      clicked: state.clicked ?? {},
    });
  }, [savingOpen, targetId, state]);

  function discardSavedChoices() {
    if (targetId && state.sourceEditionBpaId)
      clearImportChoices(targetId, state.sourceEditionBpaId);
  }

  const orgs = previewData?.orgs ?? NO_ORGS;
  const listReady = (preview.isSuccess && !preview.isPlaceholderData) || preview.isError;
  const organisations = useOrganisationsDisplay({
    sourceId: state.sourceEditionBpaId,
    listReady,
    loading: preview.isLoading,
    data: previewData?.orgs ?? null,
    reloading: preview.isPlaceholderData,
  });
  const comparing = orgs.find(org => firmKey(org) === choices.comparingKey && org.compare) ?? null;
  const comparePanel = useComparePanelMotion(comparing);
  const summary = previewData
    ? previewTotals(previewData, importState)
    : { organisations: 0, counts: NO_COUNTS };

  const dirty = exit === null && isImportDirty(choices.opened, state);
  const navGuard = useNavigationGuard(dirty, {
    allowPath: ROUTES.research.cmsBpaImportReview(editionId, bpaId),
  });
  const splitContext = useSplitImportContext({
    purpose: urlContext.purpose,
    hub: urlContext.hub,
    originalEditionBpaId: state.sourceEditionBpaId,
    targetEditionBpaId: target?.id ?? null,
  });
  const splitHint = splitContext
    ? splitImportHint(splitContext.otherCardMoved, splitContext.otherName)
    : null;
  const isSplit = urlContext.purpose === "split";
  const isMerge = urlContext.purpose === "merge";
  const fixedContent = isSplit
    ? { line: splitMoveLine(sourceName) }
    : isMerge
      ? { line: mergeMoveLine(sourceName) }
      : null;
  const mergeRows: MergeRow[] = (sourceRows ?? []).map(toMergeRow);
  const mergeBadges = Object.fromEntries(
    mergeRows.flatMap(row => {
      const badge = mergeBadge(row, mergeRows, {
        editionId: sourceEditionId,
        flagOn: hasFlag("research-cms-1-enabled"),
      });
      return badge ? [[row.id, badge]] : [];
    }),
  );
  const leaveDestination = cancelDestination({
    purpose: urlContext.purpose,
    hub: urlContext.hub,
    targetEditionId: editionId,
    targetBpaId: bpaId,
  });

  useEffect(() => {
    if (exit) {
      navigate(exit.to ?? ROUTES.research.cmsBpa(editionId, bpaId));
    }
  }, [exit, navigate, editionId, bpaId]);

  function summaryText(): { sentence: string; counts?: string } {
    if (!source) return { sentence: "Pick a practice area to import from." };
    if (preview.isLoading) return { sentence: "Loading organisations..." };
    if (preview.isError && !refusal) return { sentence: "The organisations could not be loaded." };
    if (preview.isPlaceholderData) return { sentence: "Updating the counts..." };
    return describeImportSummary(
      state.mode,
      summary,
      targetName,
      sourceInAnotherEdition ? sourceName : undefined,
    );
  }

  function registerCompareButton(key: string, element: HTMLButtonElement | null) {
    if (element) compareButtons.current.set(key, element);
    else compareButtons.current.delete(key);
  }

  function openCompare(key: string, trigger?: HTMLElement) {
    compareOpener.current = trigger ?? null;
    const wasOpen = comparePanel.open;
    flushSync(() => {
      comparePanel.expectMove();
      choices.setComparingKey(key);
    });
    if (!wasOpen) compareTitleRef.current?.focus({ preventScroll: true });
  }

  function closeCompareMoving() {
    flushSync(() => {
      comparePanel.expectMove();
      choices.setComparingKey(null);
    });
  }

  function closeCompare() {
    const key = choices.comparingKey;
    closeCompareMoving();
    const opener = compareOpener.current;
    const returnTo = opener?.isConnected ? opener : key ? compareButtons.current.get(key) : null;
    returnTo?.focus({ preventScroll: true });
  }

  function isCompareButton(target: Node): boolean {
    return target instanceof Element && target.closest("[data-compare-trigger]") !== null;
  }

  const compareOpen = comparePanel.open;
  const compareShellRef = comparePanel.shellRef;
  const closeOnOutsidePress = useEffectEvent((event: PointerEvent) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (compareShellRef.current?.contains(target) || isCompareButton(target)) return;
    closeCompareMoving();
  });

  useEffect(() => {
    if (!compareOpen) return;
    const listener = (event: PointerEvent) => closeOnOutsidePress(event);
    document.addEventListener("pointerdown", listener);
    return () => document.removeEventListener("pointerdown", listener);
  }, [compareOpen]);

  function openReview() {
    navigate(
      {
        pathname: ROUTES.research.cmsBpaImportReview(editionId, bpaId),
        search: searchParams.toString(),
      },
      { state: { fromImport: true, changed: dirty } },
    );
  }

  function requestCancel() {
    if (dirty) setConfirmingCancel(true);
    else setExit({ to: leaveDestination });
  }

  if (targetQuery.isLoading) {
    return (
      <CmsLayout>
        <CmsEmptyState loading message="Loading" />
      </CmsLayout>
    );
  }

  if (targetQuery.isError || !target) {
    return (
      <CmsLayout breadcrumbs={[{ label: "Import content" }]}>
        <div className={styles.error} role="alert">
          {targetQuery.isError
            ? messageOf(targetQuery.error)
            : "This practice area is not available to import into."}
        </div>
      </CmsLayout>
    );
  }

  if (!hydrated && searchParams.has("source")) {
    return (
      <CmsLayout>
        <CmsEmptyState loading message="Loading" />
      </CmsLayout>
    );
  }

  const breadcrumbs = [
    {
      label: targetQuery.data?.editionName ?? "",
      path: ROUTES.research.cmsPublication(editionId),
    },
    { label: targetName, path: ROUTES.research.cmsBpa(editionId, bpaId) },
    { label: "Import content" },
  ];
  const targetLockedReason = importBlockedReason(targetQuery.data?.editionStatus);
  const blockedMessage = targetLockedReason ?? urlContext.blocked?.reason ?? null;

  if (blockedMessage) {
    const backTo = targetLockedReason
      ? { label: "Back to practice area", path: ROUTES.research.cmsBpa(editionId, bpaId) }
      : { label: "Back to the hub", path: urlContext.blocked?.hub ?? "" };
    return (
      <CmsLayout breadcrumbs={breadcrumbs}>
        <div className={styles.column}>
          <div className={styles.heading}>
            <h1 className={styles.title}>Import content into {targetName}</h1>
          </div>
          <div className={shared.callout} role="status">
            {blockedMessage}
          </div>
          <div>
            <Button variant="outlined" color="default" onClick={() => navigate(backTo.path)}>
              {backTo.label}
            </Button>
          </div>
        </div>
      </CmsLayout>
    );
  }

  const drawnOrg = comparePanel.drawn;
  const leavePromptOpen = confirmingCancel || navGuard.blocked;

  return (
    <CmsLayout
      asideFloats
      breadcrumbs={breadcrumbs}
      aside={
        drawnOrg && (
          <ComparePanel
            org={drawnOrg}
            control={firmControl(importState, firmKey(drawnOrg))}
            onChange={control =>
              choices.update(current => withFirmControl(current, firmKey(drawnOrg), control))
            }
            sourceName={sourceName}
            targetName={targetName}
            canDelete={canReplaceTargetContent(hasPermission)}
            loading={preview.isLoading}
            onClose={closeCompare}
            open={comparePanel.open}
            moving={comparePanel.moving}
            shellRef={comparePanel.shellRef}
            titleRef={compareTitleRef}
          />
        )
      }
    >
      <div className={styles.page}>
        <div className={styles.column}>
          <div className={styles.heading}>
            <h1 className={styles.title}>Import content into {targetName}</h1>
            <p className={styles.meta}>
              {[targetQuery.data?.editionName, target.rankingEntityName]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <SourceSection
            key={sourceEditionId}
            candidates={candidates}
            initialEntityId={defaultJurisdiction(sourceEditionId, target)}
            loading={sourceInAnotherEdition && sourceBpasQuery.isLoading}
            selectedLabel={sourceName}
            publicationOptions={publicationOptions}
            publicationId={sourcePublication?.id ?? ""}
            editionOptions={editionOptions}
            editionId={sourceEditionId}
            onPublicationChange={changeSourcePublication}
            onEditionChange={changeSourceEdition}
            selectedId={state.sourceEditionBpaId}
            onSelect={pickSource}
            fixed={isSplit || isMerge}
            mergeBadges={mergeBadges}
          />
          <ContentSection
            open={source !== null}
            focusToken={choices.focus.content}
            kinds={state.kinds}
            mode={state.mode}
            moveAllowed={canMove(hasPermission)}
            moveLockedReason={moveBlockedReason(sourceEditionStatus)}
            notice={modeNotice}
            split={fixedContent}
            scoresComeAcross={scoresComeAcross}
            onToggleKind={choices.toggleKind}
            onModeChange={changeMode}
          />
          {choices.staleMessage && (
            <div className={shared.callout} role="status">
              {choices.staleMessage}
            </div>
          )}
          {refusal && (
            <div className={shared.errorBox} role="alert">
              <span>{refusal.reason}</span>
              <Button
                variant="outlined"
                color="default"
                size="small"
                onClick={() => {
                  setRefusalResets(count => count + 1);
                  choices.replaceState(resetRefusedChoices(importState, refusal));
                }}
              >
                Reset changes
              </Button>
            </div>
          )}
          <OrganisationsSection
            open={organisations.open}
            focusToken={choices.focus.orgs}
            state={importState}
            loading={organisations.loading}
            reloading={organisations.reloading}
            error={preview.isError && !refusal ? messageOf(preview.error) : null}
            orgs={organisations.orgs}
            clashCount={orgs.filter(org => org.clash).length}
            comparingKey={comparing ? firmKey(comparing) : null}
            canReplaceTargetContent={canReplaceTargetContent(hasPermission)}
            onRetry={() => preview.refetch()}
            onToggle={choices.toggleOrg}
            onToggleAll={include => choices.toggleAll(orgs, include)}
            onChoose={choices.choose}
            onStateChange={choices.replaceState}
            undoKey={`${state.sourceEditionBpaId ?? ""}:${refusalResets}`}
            onCompare={openCompare}
            registerCompareButton={registerCompareButton}
            hint={splitHint}
          />
        </div>
        <ImportSummaryBar
          summary={summaryText()}
          canReview={Boolean(source && previewData && !refusal && !preview.isPlaceholderData)}
          onCancel={requestCancel}
          onReview={openReview}
        />
      </div>

      {leavePromptOpen && (
        <UnsavedChangesModal
          onCancel={() => {
            setConfirmingCancel(false);
            navGuard.cancel();
          }}
          onDiscard={() => {
            discardSavedChoices();
            if (confirmingCancel) setExit({ to: leaveDestination });
            else navGuard.proceed();
          }}
        />
      )}
    </CmsLayout>
  );
}
